import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AdPlacementPreview from '../components/advertise/AdPlacementPreview';
import { AI_REFERRAL_SENTENCE } from '../lib/aiReferralStat';

const MAILTO = 'mailto:jaspal@savedelete.com?subject=Advertising%20on%20SaveDelete';

/* ------------------------------------------------------------------ */
/* Tentative introductory pricing — edit these numbers freely.         */
/* ------------------------------------------------------------------ */
const PRICE_SPONSOR_POST = 'from $100 / week';
const PRICE_FEATURED_AI_TOOL = '$19 / 30 days';
const PRICE_NEWSLETTER = 'from $29 / send';
const PRICE_SPONSORED_ARTICLE = 'from $99';
const PRICE_CUSTOM = "Let's talk";

const NEWSLETTER_SUBSCRIBERS = '450+';

interface InventoryItem {
  tag: string;
  title: string;
  desc: string;
  accent: string; // tailwind border/text accent
}

const INVENTORY: InventoryItem[] = [
  {
    tag: 'Homepage',
    title: 'Sponsor Post',
    desc:
      'A native card in the homepage news rail — sponsor name, headline, a short blurb and an optional logo. Carries a clear “Sponsored” disclosure, links with rel="sponsored", and refreshes weekly.',
    accent: 'amber',
  },
  {
    tag: 'Directory · live now',
    title: 'Featured AI Tool',
    desc:
      'A paid featured placement in our /ai-tools/ directory — pinned to the Featured row with a badge and top-of-category placement for 30 days. Self-serve and live now: feature a tool right from its own page.',
    accent: 'blue',
  },
  {
    tag: 'Email',
    title: 'Newsletter sponsor slot',
    desc: `A sponsored placement in our daily tech-news digest email, delivered to ${NEWSLETTER_SUBSCRIBERS} subscribers.`,
    accent: 'violet',
  },
  {
    tag: 'Long-form',
    title: 'Sponsored article',
    desc:
      'A longer-form sponsored or guest post published on SaveDelete, clearly disclosed. Great for launches, deep-dives and thought leadership.',
    accent: 'emerald',
  },
  {
    tag: 'Bespoke',
    title: 'Custom / bundle',
    desc:
      'Mix formats into a campaign, or build something bespoke across the homepage, directory and newsletter. Tell us your goal and we\'ll shape it.',
    accent: 'slate',
  },
];

const accentClasses: Record<string, { border: string; tag: string }> = {
  amber: { border: 'border-t-amber-400', tag: 'bg-amber-100 text-amber-800' },
  blue: { border: 'border-t-blue-500', tag: 'bg-blue-100 text-blue-800' },
  violet: { border: 'border-t-violet-500', tag: 'bg-violet-100 text-violet-800' },
  emerald: { border: 'border-t-emerald-500', tag: 'bg-emerald-100 text-emerald-800' },
  slate: { border: 'border-t-slate-400', tag: 'bg-slate-200 text-slate-700' },
};

interface PriceRow {
  name: string;
  desc: string;
  price: string;
  cta: { label: string; href: string; internal?: boolean };
}

const PRICING: PriceRow[] = [
  {
    name: 'Sponsor Post',
    desc: 'Native card in the homepage news rail.',
    price: PRICE_SPONSOR_POST,
    cta: { label: 'Book a slot', href: MAILTO },
  },
  {
    name: 'Featured AI Tool',
    desc: 'Featured placement in the /ai-tools/ directory.',
    price: PRICE_FEATURED_AI_TOOL,
    cta: { label: 'Feature a tool', href: '/ai-tools/', internal: true },
  },
  {
    name: 'Newsletter sponsor slot',
    desc: `Sponsored slot in the daily digest (${NEWSLETTER_SUBSCRIBERS} subscribers).`,
    price: PRICE_NEWSLETTER,
    cta: { label: 'Reserve a send', href: MAILTO },
  },
  {
    name: 'Sponsored article',
    desc: 'Longer-form sponsored or guest post.',
    price: PRICE_SPONSORED_ARTICLE,
    cta: { label: 'Pitch a post', href: MAILTO },
  },
  {
    name: 'Custom / bundle',
    desc: 'Bespoke multi-format campaigns.',
    price: PRICE_CUSTOM,
    cta: { label: 'Get in touch', href: MAILTO },
  },
];

const AdvertisePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Advertise on SaveDelete</title>
        <meta
          name="description"
          content="Reach an engaged tech audience on SaveDelete — a 15-year tech publication running a real-time, clustered tech-news river. Sponsor Posts, Featured AI Tool placements, newsletter slots and sponsored articles, all personally reviewed."
        />
        <link rel="canonical" href="https://savedelete.com/advertise/" />
        <meta property="og:title" content="Advertise on SaveDelete" />
        <meta property="og:description" content="Sponsor Posts, Featured AI Tools, newsletter slots and sponsored articles on SaveDelete — reach engaged tech readers. Personally curated, clearly disclosed, high-signal." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://savedelete.com/advertise/" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-3xl mx-auto px-4 py-16 md:py-20 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-300 mb-3">Advertise</p>
          <h1 className="text-3xl md:text-5xl font-bold mb-5">Reach a 15-year tech audience</h1>
          <p className="text-lg md:text-xl text-white/80 leading-relaxed">
            SaveDelete has covered technology since 2010. Today it runs a fast, clustered feed of
            the day's most important tech stories — a real-time news river drawn from 20+ trusted
            sources and refreshed every 15 minutes — read by engaged, high-intent tech readers.
          </p>
          <a
            href={MAILTO}
            className="inline-block mt-8 px-7 py-3 bg-blue-600 text-white text-base font-semibold rounded-full hover:bg-blue-700 shadow-lg transition-colors"
          >
            Get in touch
          </a>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 py-14 space-y-16">

        {/* Who we reach */}
        <section className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Who SaveDelete reaches</h2>
          <p className="text-slate-700 leading-relaxed">
            Our readers come for a fast, no-noise read of what's happening in technology — AI,
            big tech, startups, gadgets, security, and science. The homepage news river is updated
            around the clock, so people return daily. That makes SaveDelete a place for advertisers
            who want to reach a genuinely tech-literate audience in an editorial context, not an
            audience chasing coupons.
          </p>
          {/* Deliberately a composition claim, not a reach claim. The figures and their
              caveats live in src/lib/aiReferralStat.ts. */}
          <p className="mt-4 text-slate-700 leading-relaxed">
            <span className="font-semibold text-slate-900">Where those visits come from is changing.</span>{' '}
            {AI_REFERRAL_SENTENCE}
          </p>
        </section>

        {/* Where your placement appears */}
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Where your placement appears</h2>
          <p className="text-slate-700 leading-relaxed mb-8 max-w-3xl">
            A quick, abstracted look at the two self-serve visual placements — the homepage
            Sponsor Post and the Featured AI Tool. (Illustrative wireframes, not live content.)
          </p>
          <AdPlacementPreview />
        </section>

        {/* Inventory */}
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Ad inventory</h2>
          <p className="text-slate-700 leading-relaxed mb-8 max-w-3xl">
            Five deliverable ways to reach our audience — across the homepage, the AI-tools
            directory, the newsletter and long-form.
          </p>
          <div className="grid gap-5 sm:grid-cols-2">
            {INVENTORY.map((item) => {
              const a = accentClasses[item.accent];
              return (
                <div
                  key={item.title}
                  className={`rounded-xl border border-slate-200 border-t-4 ${a.border} bg-white p-6 shadow-sm`}
                >
                  <span className={`inline-block text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full mb-3 ${a.tag}`}>
                    {item.tag}
                  </span>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-700 leading-relaxed">{item.desc}</p>
                  {item.title === 'Featured AI Tool' && (
                    <Link to="/ai-tools/" className="inline-block mt-3 text-sm text-blue-600 font-medium hover:underline">
                      Browse the directory →
                    </Link>
                  )}
                  {item.title === 'Sponsored article' && (
                    <Link to="/publish-article/" className="inline-block mt-3 text-sm text-blue-600 font-medium hover:underline">
                      See guest-article guidelines →
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Pricing */}
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Intro pricing</h2>
          <p className="text-slate-700 leading-relaxed mb-8 max-w-3xl">
            Tentative introductory rates — contact us for a custom quote or bundle.
          </p>

          <div className="space-y-3">
            {PRICING.map((row) => (
              <div
                key={row.name}
                className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-slate-900">{row.name}</h3>
                  <p className="text-sm text-slate-600">{row.desc}</p>
                </div>
                <div className="sm:text-right">
                  <div className="text-xl font-extrabold text-blue-600 whitespace-nowrap">{row.price}</div>
                </div>
                <div className="sm:w-40 shrink-0">
                  {row.cta.internal ? (
                    <Link
                      to={row.cta.href}
                      className="block text-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-colors"
                    >
                      {row.cta.label}
                    </Link>
                  ) : (
                    <a
                      href={row.cta.href}
                      className="block text-center px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-full hover:bg-slate-700 transition-colors"
                    >
                      {row.cta.label}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Curation */}
        <section className="bg-slate-50 border border-slate-200 rounded-xl p-6 md:p-8 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Personally curated</h2>
          <p className="text-slate-700 leading-relaxed">
            Every placement is personally reviewed by our editor for relevance and quality — we
            run a tight, high-signal publication, not a banner farm. If a placement wouldn't be
            useful to a tech reader, it doesn't run. That standard is what keeps the audience
            engaged, and it's what makes a placement here worth more than a generic display
            impression.
          </p>
        </section>

        {/* CTA */}
        <section className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Interested?</h2>
          <p className="text-slate-700 leading-relaxed mb-6 max-w-2xl mx-auto">
            Tell us a little about what you'd like to promote and we'll follow up with availability
            and pricing.
          </p>
          <a
            href={MAILTO}
            className="inline-block px-8 py-3.5 bg-blue-600 text-white text-base font-semibold rounded-full hover:bg-blue-700 shadow-md transition-colors"
          >
            Email jaspal@savedelete.com
          </a>
        </section>

        {/* Guest article note */}
        <section className="border-t border-slate-200 pt-8 text-center">
          <p className="text-slate-600">
            Looking to publish a guest article instead?{' '}
            <Link to="/publish-article/" className="text-blue-600 font-medium hover:underline">
              See our guest article guidelines →
            </Link>
          </p>
        </section>

      </main>
    </div>
  );
};

export default AdvertisePage;
